import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRefinementList } from 'react-instantsearch';
import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';

import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import { currentContextAtom } from '../../state';

export default function MyScopeFilter(): React.JSX.Element {
  const { t } = useTranslation(['discovery']);
  const { userBrands, userAdministrativeAreas } = useAtomValue(currentContextAtom);
  const [isActivated, setIsActivated] = useState(true);

  const { refine: refineBrand } = useRefinementList({ attribute: 'brand', limit: 1000 });
  const { refine: refineAdministrativeArea } = useRefinementList({ attribute: 'administrative_areas', limit: 1000 });
  const currentBrandFilters = useNormalizedCurrentRefinements('brand') as string[];
  const currentAreaFilters = useNormalizedCurrentRefinements('administrative_areas') as string[];

  const areaFilterValue = (area: string): string => {
    return `administrative_area_level_2|${area!}`;
  };

  const addScopeInFilters = (): void => {
    userBrands?.forEach(({ brand, has_all_brands }) => {
      if (!has_all_brands && !currentBrandFilters.includes(brand!)) {
        refineBrand(brand!);
      }
    });

    userAdministrativeAreas?.forEach(({ area, has_all_areas }) => {
      if (!has_all_areas && !currentAreaFilters.includes(areaFilterValue(area!))) {
        refineAdministrativeArea(areaFilterValue(area!));
      }
    });
  };

  const removeScopeInFilters = (): void => {
    userBrands?.forEach(({ brand }) => {
      if (currentBrandFilters.includes(brand!)) {
        refineBrand(brand!);
      }
    });

    userAdministrativeAreas?.forEach(({ area }) => {
      if (currentAreaFilters.includes(areaFilterValue(area!))) {
        refineAdministrativeArea(areaFilterValue(area!));
      }
    });
  };

  // If something from the user scope is changed in the filters list we should update the filter activated mark
  useEffect(() => {
    const allBrandsScopeAreInFilters = userBrands?.filter(({ has_all_brands }) => !has_all_brands).every(
      ({ brand }) => currentBrandFilters.includes(brand!),
    );
    const allAreasScopeAreInFilters = userAdministrativeAreas?.filter(({ has_all_areas }) => !has_all_areas)?.every(
      ({ area }) => currentAreaFilters.includes(areaFilterValue(area!)),
    );

    const scopeIsInFilters = allBrandsScopeAreInFilters && allAreasScopeAreInFilters;

    if (isActivated && !scopeIsInFilters) {
      setIsActivated(false);
    }

    if (!isActivated && scopeIsInFilters) {
      setIsActivated(true);
    }
  }, [currentBrandFilters, currentAreaFilters]);

  const toggleFilter = (): void => {
    const isNowActivated = !isActivated;
    setIsActivated(isNowActivated);

    if (isNowActivated) {
      addScopeInFilters();
    } else {
      removeScopeInFilters();
    }
  };

  return (
    <Chip
      variant="filter"
      className={isActivated ? 'Kheops-selected' : ''}
      label={t('discovery:my_scope')}
      onClick={toggleFilter}
    />
  );
}
